import axios from 'axios'

export type CurrencyInfo = {
  localCurrency: string
  localCurrencyName?: string
  localSymbol?: string
  visitorCurrency: string
  exchangeRate: number
  rateAvailable: boolean
  rateDate?: string
  countryName?: string
}

const sharedCurrencyCountries: Record<string, string[]> = {
  EUR: [
    'AD',
    'AT',
    'BE',
    'CY',
    'DE',
    'EE',
    'ES',
    'FI',
    'FR',
    'GR',
    'HR',
    'IE',
    'IT',
    'LT',
    'LU',
    'LV',
    'MC',
    'ME',
    'MT',
    'NL',
    'PT',
    'SI',
    'SK',
    'SM',
    'VA',
    'XK'
  ],
  USD: ['US', 'EC', 'SV', 'PA', 'TL', 'MH', 'FM', 'PW', 'BQ'],
  XOF: ['BJ', 'BF', 'CI', 'GW', 'ML', 'NE', 'SN', 'TG'],
  XAF: ['CM', 'CF', 'TD', 'CG', 'GQ', 'GA'],
  XCD: ['AG', 'DM', 'GD', 'KN', 'LC', 'VC'],
  AUD: ['AU', 'KI', 'NR', 'TV'],
  NZD: ['NZ', 'CK', 'NU', 'TK'],
  CHF: ['CH', 'LI']
}

const countryCurrency: Record<string, string> = {
  AF: 'AFN',
  AL: 'ALL',
  DZ: 'DZD',
  AO: 'AOA',
  AR: 'ARS',
  AM: 'AMD',
  AZ: 'AZN',
  BS: 'BSD',
  BH: 'BHD',
  BD: 'BDT',
  BB: 'BBD',
  BY: 'BYN',
  BZ: 'BZD',
  BT: 'BTN',
  BO: 'BOB',
  BA: 'BAM',
  BW: 'BWP',
  BR: 'BRL',
  BN: 'BND',
  BG: 'BGN',
  BI: 'BIF',
  CV: 'CVE',
  KH: 'KHR',
  CA: 'CAD',
  CL: 'CLP',
  CN: 'CNY',
  CO: 'COP',
  KM: 'KMF',
  CD: 'CDF',
  CR: 'CRC',
  CU: 'CUP',
  CZ: 'CZK',
  DJ: 'DJF',
  DO: 'DOP',
  EG: 'EGP',
  ER: 'ERN',
  SZ: 'SZL',
  ET: 'ETB',
  FJ: 'FJD',
  GM: 'GMD',
  GE: 'GEL',
  GH: 'GHS',
  GT: 'GTQ',
  GN: 'GNF',
  GY: 'GYD',
  HT: 'HTG',
  HN: 'HNL',
  HK: 'HKD',
  HU: 'HUF',
  IS: 'ISK',
  IN: 'INR',
  ID: 'IDR',
  IR: 'IRR',
  IQ: 'IQD',
  IL: 'ILS',
  JM: 'JMD',
  JP: 'JPY',
  JO: 'JOD',
  KZ: 'KZT',
  KE: 'KES',
  KP: 'KPW',
  KR: 'KRW',
  KW: 'KWD',
  KG: 'KGS',
  LA: 'LAK',
  LB: 'LBP',
  LS: 'LSL',
  LR: 'LRD',
  LY: 'LYD',
  MG: 'MGA',
  MW: 'MWK',
  MY: 'MYR',
  MV: 'MVR',
  MR: 'MRU',
  MU: 'MUR',
  MX: 'MXN',
  MD: 'MDL',
  MN: 'MNT',
  MA: 'MAD',
  MZ: 'MZN',
  MM: 'MMK',
  NA: 'NAD',
  NP: 'NPR',
  NI: 'NIO',
  NG: 'NGN',
  MK: 'MKD',
  NO: 'NOK',
  OM: 'OMR',
  PK: 'PKR',
  PG: 'PGK',
  PY: 'PYG',
  PE: 'PEN',
  PH: 'PHP',
  PL: 'PLN',
  QA: 'QAR',
  RO: 'RON',
  RU: 'RUB',
  RW: 'RWF',
  ST: 'STN',
  SA: 'SAR',
  RS: 'RSD',
  SC: 'SCR',
  SL: 'SLE',
  SG: 'SGD',
  SB: 'SBD',
  SO: 'SOS',
  ZA: 'ZAR',
  SS: 'SSP',
  LK: 'LKR',
  SD: 'SDG',
  SR: 'SRD',
  SE: 'SEK',
  SY: 'SYP',
  TW: 'TWD',
  TJ: 'TJS',
  TZ: 'TZS',
  TH: 'THB',
  TO: 'TOP',
  TT: 'TTD',
  TN: 'TND',
  TR: 'TRY',
  TM: 'TMT',
  UG: 'UGX',
  UA: 'UAH',
  AE: 'AED',
  GB: 'GBP',
  UY: 'UYU',
  UZ: 'UZS',
  VU: 'VUV',
  VE: 'VES',
  VN: 'VND',
  WS: 'WST',
  YE: 'YER',
  ZM: 'ZMW',
  ZW: 'ZWG'
}

const getCurrencyForCountry = (countryCode?: string) => {
  if (!countryCode) return ''

  const normalized = countryCode.toUpperCase()
  if (countryCurrency[normalized]) return countryCurrency[normalized]

  const shared = Object.entries(sharedCurrencyCountries).find(([, countries]) =>
    countries.includes(normalized)
  )

  return shared?.[0] || ''
}

const getCurrencySymbol = (currency: string) => {
  try {
    return (
      new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol'
      })
        .formatToParts(1)
        .find((part) => part.type === 'currency')?.value || currency
    )
  } catch {
    return currency
  }
}

const MAPBOX_ACCESS_TOKEN =
  'pk.eyJ1IjoibGF1cmFoZGV2IiwiYSI6ImNsY3BtMTN4dDFueG0zcG81em41YXh4emIifQ.eGKakvFNW6mcmRuHze23mg'

type DestinationCountry = {
  countryCode?: string
  countryName?: string
}

const getDestinationCountry = async (
  latitude: number,
  longitude: number
): Promise<DestinationCountry> => {
  try {
    const mapboxResult = await axios.get(
      'https://api.mapbox.com/search/geocode/v6/reverse',
      {
        params: {
          latitude,
          longitude,
          language: 'pt',
          access_token: MAPBOX_ACCESS_TOKEN
        },
        timeout: 8000
      }
    )

    const features = Array.isArray(mapboxResult.data?.features)
      ? mapboxResult.data.features
      : []
    const countryContext = features
      .map((feature: any) => feature?.properties?.context?.country)
      .find(Boolean)
    const countryFeature = features.find(
      (feature: any) => feature?.properties?.feature_type === 'country'
    )
    const featureProperties = countryFeature?.properties || {}
    const countryCode = String(
      countryContext?.country_code ||
        featureProperties?.country_code ||
        featureProperties?.short_code ||
        ''
    )
      .slice(0, 2)
      .toUpperCase()
    const countryName =
      countryContext?.name ||
      featureProperties?.name_preferred ||
      featureProperties?.name

    if (countryCode) return { countryCode, countryName }
  } catch {
    // Fall through to the secondary reverse-geocoding provider.
  }

  try {
    const destinationResult = await axios.get(
      'https://api.bigdatacloud.net/data/reverse-geocode-client',
      {
        params: {
          latitude,
          longitude,
          localityLanguage: 'pt'
        },
        timeout: 8000
      }
    )

    const countryCode = String(destinationResult.data?.countryCode || '')
      .slice(0, 2)
      .toUpperCase()
    const countryName = destinationResult.data?.countryName

    if (countryCode) return { countryCode, countryName }
  } catch {
    // A destination without country resolution should display an unavailable state,
    // never a guessed currency.
  }

  return {}
}

export const getCurrencyInfo = async (
  latitude: number,
  longitude: number,
  visitorCurrency = 'BRL'
): Promise<CurrencyInfo> => {
  const { countryCode, countryName } = await getDestinationCountry(
    latitude,
    longitude
  )
  const localCurrency = countryCode ? getCurrencyForCountry(countryCode) : ''
  const normalizedVisitorCurrency = (visitorCurrency || 'BRL').toUpperCase()
  const localSymbol = localCurrency ? getCurrencySymbol(localCurrency) : ''
  const localCurrencyName =
    countryName && localCurrency ? `Moeda oficial de ${countryName}` : undefined

  if (!localCurrency) {
    return {
      localCurrency: '',
      localCurrencyName,
      localSymbol,
      visitorCurrency: normalizedVisitorCurrency,
      exchangeRate: 0,
      rateAvailable: false,
      countryName
    }
  }

  if (localCurrency === normalizedVisitorCurrency) {
    return {
      localCurrency,
      localCurrencyName,
      localSymbol,
      visitorCurrency: normalizedVisitorCurrency,
      exchangeRate: 1,
      rateAvailable: true,
      countryName
    }
  }

  try {
    const rateResult = await axios.get(
      `https://api.frankfurter.dev/v2/rate/${normalizedVisitorCurrency.toLowerCase()}/${localCurrency.toLowerCase()}`,
      { timeout: 8000 }
    )

    const exchangeRate = Number(rateResult.data?.rate)

    return {
      localCurrency,
      localCurrencyName,
      localSymbol,
      visitorCurrency: normalizedVisitorCurrency,
      exchangeRate: Number.isFinite(exchangeRate) ? exchangeRate : 0,
      rateAvailable: Number.isFinite(exchangeRate),
      rateDate: rateResult.data?.date,
      countryName
    }
  } catch {
    return {
      localCurrency,
      localCurrencyName,
      localSymbol,
      visitorCurrency: normalizedVisitorCurrency,
      exchangeRate: 0,
      rateAvailable: false,
      countryName
    }
  }
}
